// Curated map of well-known providers to Simple Icons CDN slugs. Hits
// against this map render the official brand SVG (in brand colour) on
// a white tile — the same look as the reference UI. Entries that don't
// match here fall through to a Google favicon lookup, then finally to
// the brand-green initials avatar.
//
// Adding a provider: pick the Simple Icons slug from
// https://simpleicons.org and list a few `aliases` (lowercase fragments
// of the entry name) plus the platform's primary `domains`. For
// providers not on Simple Icons (internal tools, regional brands), set
// `svg` to inline SVG markup — it'll be inlined as a data URI.
//
// Privacy note: matched providers fetch from cdn.simpleicons.org
// (anonymous CDN of public brand SVGs). Only unknown providers leak
// the domain to Google via the favicon fallback.

// Hospital cross — used for healthcare/clinical tools that aren't in
// Simple Icons. Brand-green stroke on a transparent ground so the white
// avatar tile shows through.
const HOSPITAL_ICON_SVG = `
<svg xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="#0e5b46" stroke-width="2" stroke-linecap="round" stroke-linejoin="round">
  <path d="M4 21V8a1 1 0 0 1 1-1h14a1 1 0 0 1 1 1v13"/>
  <path d="M3 21h18"/>
  <path d="M9 21v-4a1 1 0 0 1 1-1h4a1 1 0 0 1 1 1v4"/>
  <path d="M12 4v2"/>
  <path d="M11 5h2"/>
  <path d="M12 10v4"/>
  <path d="M10 12h4"/>
</svg>`.trim()

const PROVIDERS = {
  // Payment gateways
  razorpay:    { aliases: ['razorpay'],          domains: ['razorpay.com'] },
  stripe:      { aliases: ['stripe'],            domains: ['stripe.com'] },
  paypal:      { aliases: ['paypal'],            domains: ['paypal.com'] },
  payu:        { aliases: ['payu'],              domains: ['payu.in', 'payu.com'] },
  cashfree:    { aliases: ['cashfree'],          domains: ['cashfree.com'] },
  paytm:       { aliases: ['paytm'],             domains: ['paytm.com'] },
  phonepe:     { aliases: ['phonepe'],           domains: ['phonepe.com'] },

  // Email / Workspace
  gmail:           { aliases: ['gmail'],         domains: ['mail.google.com'] },
  googleworkspace: { aliases: ['workspace', 'gsuite', 'g suite'], domains: ['workspace.google.com', 'admin.google.com'] },
  microsoftoutlook:{ aliases: ['outlook'],       domains: ['outlook.com', 'outlook.live.com'] },
  zoho:        { aliases: ['zoho'],              domains: ['zoho.com', 'zoho.in'] },

  // Hosting / Cloud / Domains
  amazonaws:   { aliases: ['aws', 'amazon web'], domains: ['aws.amazon.com', 'amazonaws.com', 'console.aws.amazon.com'] },
  googlecloud: { aliases: ['gcp', 'google cloud'], domains: ['cloud.google.com', 'console.cloud.google.com'] },
  microsoftazure: { aliases: ['azure'],          domains: ['azure.com', 'portal.azure.com'] },
  cloudflare:  { aliases: ['cloudflare'],        domains: ['cloudflare.com', 'dash.cloudflare.com'] },
  digitalocean:{ aliases: ['digitalocean', 'do '], domains: ['digitalocean.com'] },
  vercel:      { aliases: ['vercel'],            domains: ['vercel.com'] },
  netlify:     { aliases: ['netlify'],           domains: ['netlify.com'] },
  namecheap:   { aliases: ['namecheap'],         domains: ['namecheap.com'] },
  godaddy:     { aliases: ['godaddy'],           domains: ['godaddy.com'] },
  hetzner:     { aliases: ['hetzner'],           domains: ['hetzner.com'] },
  hostinger:   { aliases: ['hostinger'],         domains: ['hostinger.com', 'hostinger.in'] },

  // Database
  mysql:       { aliases: ['mysql'],             domains: ['mysql.com'] },
  postgresql:  { aliases: ['postgres'],          domains: ['postgresql.org'] },
  mongodb:     { aliases: ['mongo'],             domains: ['mongodb.com'] },
  redis:       { aliases: ['redis'],             domains: ['redis.io', 'redis.com'] },

  // API / Comms
  twilio:      { aliases: ['twilio'],            domains: ['twilio.com'] },
  sendgrid:    { aliases: ['sendgrid'],          domains: ['sendgrid.com'] },
  mailgun:     { aliases: ['mailgun'],           domains: ['mailgun.com'] },
  msg91:       { aliases: ['msg91'],             domains: ['msg91.com'] },
  exotel:      { aliases: ['exotel'],            domains: ['exotel.com'] },

  // Shipping (India focus)
  dhl:         { aliases: ['dhl'],               domains: ['dhl.com'] },
  fedex:       { aliases: ['fedex'],             domains: ['fedex.com'] },
  // Bluedart, Delhivery and Shiprocket aren't on Simple Icons; favicon
  // fallback will handle them when a URL is set.

  // Social / Ads
  facebook:    { aliases: ['facebook', 'fb '],   domains: ['facebook.com'] },
  instagram:   { aliases: ['instagram'],         domains: ['instagram.com'] },
  linkedin:    { aliases: ['linkedin'],          domains: ['linkedin.com'] },
  x:           { aliases: ['twitter', 'x.com'],  domains: ['x.com', 'twitter.com'] },
  googleads:   { aliases: ['google ads', 'adwords'], domains: ['ads.google.com'] },
  meta:        { aliases: ['meta ads', 'meta business'], domains: ['business.facebook.com'] },

  // Marketplaces / e-commerce
  //
  // Flipkart, Amazon-Seller-Central and Meesho aren't in Simple Icons
  // (no official permissive-licence brand SVG). Add a URL to the entry
  // and the Google favicon fallback will surface the brand's mark
  // automatically — no need to list them here.

  // Analytics
  googleanalytics: { aliases: ['analytics', 'ga4'], domains: ['analytics.google.com'] },
  posthog:     { aliases: ['posthog'],           domains: ['posthog.com', 'us.posthog.com', 'eu.posthog.com'] },
  mixpanel:    { aliases: ['mixpanel'],          domains: ['mixpanel.com'] },
  hotjar:      { aliases: ['hotjar'],            domains: ['hotjar.com'] },

  // Storage
  googledrive: { aliases: ['drive', 'google drive'], domains: ['drive.google.com'] },
  dropbox:     { aliases: ['dropbox'],           domains: ['dropbox.com'] },

  // Healthcare / Clinical (not on Simple Icons — hand-rolled icon)
  pulsesuite:  { aliases: ['pulsesuite', 'pulse suite'], svg: HOSPITAL_ICON_SVG },

  // Developer / Collab
  github:      { aliases: ['github'],            domains: ['github.com'] },
  gitlab:      { aliases: ['gitlab'],            domains: ['gitlab.com'] },
  bitbucket:   { aliases: ['bitbucket'],         domains: ['bitbucket.org'] },
  slack:       { aliases: ['slack'],             domains: ['slack.com'] },
  notion:      { aliases: ['notion'],            domains: ['notion.so'] },
  jira:        { aliases: ['jira'],              domains: ['atlassian.net'] },
}

function extractDomain(url) {
  if (!url || typeof url !== 'string') {
    return ''
  }
  try {
    // The URL constructor needs a scheme — prepend https:// if absent
    // so plain entries like "namecheap.com/dashboard" still resolve.
    const withScheme = /^[a-z]+:\/\//i.test(url) ? url : `https://${url}`
    const { hostname } = new URL(withScheme)
    return hostname.toLowerCase().replace(/^www\./, '')
  } catch (e) {
    return ''
  }
}

function normalise(text) {
  return (text || '').toString().toLowerCase()
}

// Returns the best available logo for an entry, or `null` if nothing
// matched and the caller should render the initials avatar instead.
//
// Shape: { src: string, source: 'curated' | 'favicon' }
//
// `excludeSources` lets the caller skip sources that already failed —
// for example, when a curated Simple Icons URL 404s the component can
// re-resolve with `['curated']` excluded so the favicon fallback runs
// instead of jumping straight to initials.
export function resolveProviderLogo(entry, excludeSources = []) {
  if (!entry) {
    return null
  }

  const nameKey = normalise(entry.name)
  const domain = extractDomain(entry.url)

  if (!excludeSources.includes('curated')) {
    for (const [slug, def] of Object.entries(PROVIDERS)) {
      const nameHit = def.aliases && def.aliases.some((a) => nameKey.includes(a))
      const domainHit = domain && def.domains && def.domains.some((d) => domain === d || domain.endsWith(`.${d}`))
      if (!nameHit && !domainHit) {
        continue
      }
      if (def.svg) {
        return { src: `data:image/svg+xml;utf8,${encodeURIComponent(def.svg)}`, source: 'curated' }
      }
      return { src: `https://cdn.simpleicons.org/${slug}`, source: 'curated' }
    }
  }

  if (!excludeSources.includes('favicon') && domain) {
    // sz=128 — Google serves the closest size it has; the larger ask
    // avoids the upscaled-16px pixelated look on sites that only ship
    // a tiny .ico. Falls through to initials if the favicon 404s.
    return {
      src: `https://www.google.com/s2/favicons?domain=${encodeURIComponent(domain)}&sz=128`,
      source: 'favicon',
    }
  }

  return null
}
